/**
 * Phase 4 — seed inspection-template presets.
 *
 * Every new org gets these four templates seeded as **drafts** during
 * org creation (the admin clones + customizes + publishes). The slugs
 * are stable so the re-seed endpoint can upsert by `(org_id, slug)`
 * without overwriting admin edits.
 *
 * The DHS Unit Inspection is intentionally a generic shelter-style unit
 * inspection — it resembles standard housing inspection forms without
 * claiming to be the actual NYC DHS form (which is a controlled
 * document).
 */

import { dhsOfficialPreset } from './dhs-official-preset.js';
import type { InspectionPreset } from './inspection-constants.js';

export type { InspectionPreset } from './inspection-constants.js';

// Stable, hand-picked UUIDs so re-running the seeder produces identical
// `question_id` values across orgs. (uuidv7 not strictly required here
// since these IDs only need to be unique within the schema.)
const ID = {
  dhs: {
    s_general: '01900000-0000-7000-8000-000000000001',
    q_unit_clean: '01900000-0000-7000-8000-000000000002',
    q_walls_ceiling: '01900000-0000-7000-8000-000000000003',
    q_floors: '01900000-0000-7000-8000-000000000004',
    q_windows_screens: '01900000-0000-7000-8000-000000000005',
    q_lighting: '01900000-0000-7000-8000-000000000006',
    q_general_photos: '01900000-0000-7000-8000-000000000007',
    s_kitchen: '01900000-0000-7000-8000-000000000010',
    q_kitchen_stove: '01900000-0000-7000-8000-000000000011',
    q_kitchen_fridge: '01900000-0000-7000-8000-000000000012',
    q_kitchen_sink: '01900000-0000-7000-8000-000000000013',
    q_kitchen_pests: '01900000-0000-7000-8000-000000000014',
    q_kitchen_photos: '01900000-0000-7000-8000-000000000015',
    s_bathroom: '01900000-0000-7000-8000-000000000020',
    q_bath_toilet: '01900000-0000-7000-8000-000000000021',
    q_bath_sink: '01900000-0000-7000-8000-000000000022',
    q_bath_tub: '01900000-0000-7000-8000-000000000023',
    q_bath_mold: '01900000-0000-7000-8000-000000000024',
    q_bath_photos: '01900000-0000-7000-8000-000000000025',
    s_bedrooms: '01900000-0000-7000-8000-000000000030',
    q_bed_count: '01900000-0000-7000-8000-000000000031',
    q_bed_smoke: '01900000-0000-7000-8000-000000000032',
    q_bed_co: '01900000-0000-7000-8000-000000000033',
    q_bed_window_guards: '01900000-0000-7000-8000-000000000034',
    s_safety: '01900000-0000-7000-8000-000000000040',
    q_safety_extinguisher: '01900000-0000-7000-8000-000000000041',
    q_safety_egress: '01900000-0000-7000-8000-000000000042',
    q_safety_locks: '01900000-0000-7000-8000-000000000043',
    s_pests: '01900000-0000-7000-8000-000000000050',
    q_pests_roaches: '01900000-0000-7000-8000-000000000051',
    q_pests_rodents: '01900000-0000-7000-8000-000000000052',
    q_pests_bedbugs: '01900000-0000-7000-8000-000000000053',
    q_pests_notes: '01900000-0000-7000-8000-000000000054',
    s_signoff: '01900000-0000-7000-8000-000000000060',
    q_signoff_notes: '01900000-0000-7000-8000-000000000061',
    q_signoff_signature: '01900000-0000-7000-8000-000000000062',
  },
  fire: {
    s_extinguishers: '01900000-0000-7000-8000-000000000101',
    q_ext_present: '01900000-0000-7000-8000-000000000102',
    q_ext_pressure: '01900000-0000-7000-8000-000000000103',
    q_ext_tag: '01900000-0000-7000-8000-000000000104',
    q_ext_photo: '01900000-0000-7000-8000-000000000105',
    s_signs: '01900000-0000-7000-8000-000000000110',
    q_exit_signs: '01900000-0000-7000-8000-000000000111',
    q_emergency_lights: '01900000-0000-7000-8000-000000000112',
    s_doors: '01900000-0000-7000-8000-000000000120',
    q_fire_doors: '01900000-0000-7000-8000-000000000121',
    q_door_closers: '01900000-0000-7000-8000-000000000122',
    s_sprinklers: '01900000-0000-7000-8000-000000000130',
    q_sprinkler_visible: '01900000-0000-7000-8000-000000000131',
    q_sprinkler_obstruct: '01900000-0000-7000-8000-000000000132',
    s_notes: '01900000-0000-7000-8000-000000000140',
    q_notes_text: '01900000-0000-7000-8000-000000000141',
  },
  common: {
    s_lobby: '01900000-0000-7000-8000-000000000201',
    q_lobby_clean: '01900000-0000-7000-8000-000000000202',
    q_lobby_lighting: '01900000-0000-7000-8000-000000000203',
    s_hallways: '01900000-0000-7000-8000-000000000210',
    q_hall_clean: '01900000-0000-7000-8000-000000000211',
    q_hall_lighting: '01900000-0000-7000-8000-000000000212',
    s_stairs: '01900000-0000-7000-8000-000000000220',
    q_stairs_clean: '01900000-0000-7000-8000-000000000221',
    q_stairs_obstruct: '01900000-0000-7000-8000-000000000222',
    s_laundry: '01900000-0000-7000-8000-000000000230',
    q_laundry_clean: '01900000-0000-7000-8000-000000000231',
    q_laundry_lint: '01900000-0000-7000-8000-000000000232',
    s_exterior: '01900000-0000-7000-8000-000000000240',
    q_ext_clean: '01900000-0000-7000-8000-000000000241',
    q_ext_trash: '01900000-0000-7000-8000-000000000242',
    s_photos: '01900000-0000-7000-8000-000000000250',
    q_photos: '01900000-0000-7000-8000-000000000251',
  },
  moveout: {
    s_kitchen: '01900000-0000-7000-8000-000000000301',
    q_k_appliances: '01900000-0000-7000-8000-000000000302',
    q_k_cabinets: '01900000-0000-7000-8000-000000000303',
    q_k_floor: '01900000-0000-7000-8000-000000000304',
    s_bath: '01900000-0000-7000-8000-000000000310',
    q_b_fixtures: '01900000-0000-7000-8000-000000000311',
    q_b_walls: '01900000-0000-7000-8000-000000000312',
    s_living: '01900000-0000-7000-8000-000000000320',
    q_l_walls: '01900000-0000-7000-8000-000000000321',
    q_l_floor: '01900000-0000-7000-8000-000000000322',
    s_bed: '01900000-0000-7000-8000-000000000330',
    q_bd_walls: '01900000-0000-7000-8000-000000000331',
    q_bd_floor: '01900000-0000-7000-8000-000000000332',
    s_overall: '01900000-0000-7000-8000-000000000340',
    q_o_keys: '01900000-0000-7000-8000-000000000341',
    q_o_cleaned: '01900000-0000-7000-8000-000000000342',
    q_o_photos: '01900000-0000-7000-8000-000000000343',
    q_o_signature: '01900000-0000-7000-8000-000000000344',
  },
} as const;

const dhsPreset: InspectionPreset = {
  slug: 'dhs-unit-inspection',
  name: 'DHS Unit Inspection',
  description:
    'Generic shelter-style unit inspection: general condition, kitchen, bathroom, bedrooms, safety, pests. Customize for your organization before publishing.',
  appliesTo: 'unit',
  schema: {
    sections: [
      {
        id: ID.dhs.s_general,
        title: 'General Condition',
        questions: [
          {
            id: ID.dhs.q_unit_clean,
            kind: 'pass_fail',
            label: 'Unit overall cleanliness',
            required: true,
          },
          {
            id: ID.dhs.q_walls_ceiling,
            kind: 'pass_fail',
            label: 'Walls and ceilings (no peeling, holes, water damage)',
            required: true,
          },
          {
            id: ID.dhs.q_floors,
            kind: 'pass_fail',
            label: 'Floors (no damage, trip hazards)',
            required: true,
          },
          {
            id: ID.dhs.q_windows_screens,
            kind: 'yes_no',
            label: 'All windows operate and have intact screens',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.dhs.q_lighting,
            kind: 'yes_no',
            label: 'All light fixtures functional',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.dhs.q_general_photos,
            kind: 'photo',
            label: 'General condition photos',
            minPhotos: 1,
            required: false,
          },
        ],
      },
      {
        id: ID.dhs.s_kitchen,
        title: 'Kitchen',
        questions: [
          {
            id: ID.dhs.q_kitchen_stove,
            kind: 'pass_fail',
            label: 'Stove / range operational and clean',
            required: true,
          },
          {
            id: ID.dhs.q_kitchen_fridge,
            kind: 'pass_fail',
            label: 'Refrigerator operational and clean',
            required: true,
          },
          {
            id: ID.dhs.q_kitchen_sink,
            kind: 'yes_no',
            label: 'Sink and faucet free of leaks',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.dhs.q_kitchen_pests,
            kind: 'yes_no',
            label: 'Visible pest activity in kitchen',
            defectIf: 'yes',
            required: true,
          },
          {
            id: ID.dhs.q_kitchen_photos,
            kind: 'photo',
            label: 'Kitchen photos',
            minPhotos: 1,
            required: false,
          },
        ],
      },
      {
        id: ID.dhs.s_bathroom,
        title: 'Bathroom',
        questions: [
          {
            id: ID.dhs.q_bath_toilet,
            kind: 'pass_fail',
            label: 'Toilet operational',
            required: true,
          },
          {
            id: ID.dhs.q_bath_sink,
            kind: 'yes_no',
            label: 'Sink free of leaks',
            defectIf: 'no',
            required: true,
          },
          { id: ID.dhs.q_bath_tub, kind: 'pass_fail', label: 'Tub / shower operational', required: true },
          {
            id: ID.dhs.q_bath_mold,
            kind: 'yes_no',
            label: 'Visible mold or mildew',
            defectIf: 'yes',
            required: true,
          },
          {
            id: ID.dhs.q_bath_photos,
            kind: 'photo',
            label: 'Bathroom photos',
            minPhotos: 1,
            required: false,
          },
        ],
      },
      {
        id: ID.dhs.s_bedrooms,
        title: 'Bedrooms',
        questions: [
          {
            id: ID.dhs.q_bed_count,
            kind: 'number',
            label: 'Number of bedrooms inspected',
            min: 0,
            max: 20,
            required: true,
          },
          {
            id: ID.dhs.q_bed_smoke,
            kind: 'yes_no',
            label: 'Working smoke detector in each bedroom',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.dhs.q_bed_co,
            kind: 'yes_no',
            label: 'Working CO detector on each sleeping floor',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.dhs.q_bed_window_guards,
            kind: 'yes_no',
            label: 'Window guards installed where required',
            defectIf: 'no',
            required: true,
          },
        ],
      },
      {
        id: ID.dhs.s_safety,
        title: 'Safety',
        questions: [
          {
            id: ID.dhs.q_safety_extinguisher,
            kind: 'yes_no',
            label: 'Fire extinguisher present and within service date',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.dhs.q_safety_egress,
            kind: 'yes_no',
            label: 'Means of egress unobstructed',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.dhs.q_safety_locks,
            kind: 'yes_no',
            label: 'Door locks functional, no double-cylinder dead bolts',
            defectIf: 'no',
            required: true,
          },
        ],
      },
      {
        id: ID.dhs.s_pests,
        title: 'Pests',
        questions: [
          {
            id: ID.dhs.q_pests_roaches,
            kind: 'rating',
            label: 'Roach activity (1 = none, 5 = severe)',
            scale: 5,
            defectAtOrBelow: 2,
            required: true,
            helpText: '1 = no evidence; 5 = active infestation. Scores ≤ 2 trigger a pest WO.',
          },
          {
            id: ID.dhs.q_pests_rodents,
            kind: 'yes_no',
            label: 'Rodent evidence (droppings, gnawing)',
            defectIf: 'yes',
            required: true,
          },
          {
            id: ID.dhs.q_pests_bedbugs,
            kind: 'yes_no',
            label: 'Bedbug evidence',
            defectIf: 'yes',
            required: true,
          },
          {
            id: ID.dhs.q_pests_notes,
            kind: 'long_text',
            label: 'Pest control notes',
            required: false,
          },
        ],
      },
      {
        id: ID.dhs.s_signoff,
        title: 'Sign-off',
        questions: [
          {
            id: ID.dhs.q_signoff_notes,
            kind: 'long_text',
            label: 'Inspector notes',
            required: false,
          },
          {
            id: ID.dhs.q_signoff_signature,
            kind: 'signature',
            label: 'Inspector signature',
            required: true,
          },
        ],
      },
    ],
  },
};

const fireSafetyPreset: InspectionPreset = {
  slug: 'fire-safety-walk',
  name: 'Fire Safety Walk',
  description: 'Common-area fire-safety walkthrough. Extinguishers, exit signs, fire doors, sprinklers.',
  appliesTo: 'common_area',
  schema: {
    sections: [
      {
        id: ID.fire.s_extinguishers,
        title: 'Fire Extinguishers',
        questions: [
          {
            id: ID.fire.q_ext_present,
            kind: 'yes_no',
            label: 'Extinguisher present at all required locations',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.fire.q_ext_pressure,
            kind: 'pass_fail',
            label: 'Pressure gauge in green zone',
            required: true,
          },
          {
            id: ID.fire.q_ext_tag,
            kind: 'yes_no',
            label: 'Inspection tag current',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.fire.q_ext_photo,
            kind: 'photo',
            label: 'Extinguisher tag photo',
            minPhotos: 1,
            required: false,
          },
        ],
      },
      {
        id: ID.fire.s_signs,
        title: 'Exit Signs & Emergency Lights',
        questions: [
          {
            id: ID.fire.q_exit_signs,
            kind: 'yes_no',
            label: 'All exit signs illuminated',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.fire.q_emergency_lights,
            kind: 'pass_fail',
            label: 'Emergency lights pass push-to-test',
            required: true,
          },
        ],
      },
      {
        id: ID.fire.s_doors,
        title: 'Fire Doors',
        questions: [
          {
            id: ID.fire.q_fire_doors,
            kind: 'pass_fail',
            label: 'Fire doors latch and self-close',
            required: true,
          },
          {
            id: ID.fire.q_door_closers,
            kind: 'yes_no',
            label: 'Doors propped open or held by unauthorized device',
            defectIf: 'yes',
            required: true,
          },
        ],
      },
      {
        id: ID.fire.s_sprinklers,
        title: 'Sprinkler Heads',
        questions: [
          {
            id: ID.fire.q_sprinkler_visible,
            kind: 'yes_no',
            label: 'Sprinkler heads visible and unobstructed',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.fire.q_sprinkler_obstruct,
            kind: 'yes_no',
            label: 'Storage within 18" of sprinkler heads',
            defectIf: 'yes',
            required: true,
          },
        ],
      },
      {
        id: ID.fire.s_notes,
        title: 'Notes',
        questions: [
          {
            id: ID.fire.q_notes_text,
            kind: 'long_text',
            label: 'Walkthrough notes',
            required: false,
          },
        ],
      },
    ],
  },
};

const commonAreaPreset: InspectionPreset = {
  slug: 'common-area-cleanliness',
  name: 'Common Area Cleanliness',
  description: 'Lobby, hallways, stairs, laundry, and exterior cleanliness checks.',
  appliesTo: 'common_area',
  schema: {
    sections: [
      {
        id: ID.common.s_lobby,
        title: 'Lobby',
        questions: [
          { id: ID.common.q_lobby_clean, kind: 'pass_fail', label: 'Lobby clean and tidy', required: true },
          {
            id: ID.common.q_lobby_lighting,
            kind: 'yes_no',
            label: 'All lobby lights working',
            defectIf: 'no',
            required: true,
          },
        ],
      },
      {
        id: ID.common.s_hallways,
        title: 'Hallways',
        questions: [
          { id: ID.common.q_hall_clean, kind: 'pass_fail', label: 'Hallways clean', required: true },
          {
            id: ID.common.q_hall_lighting,
            kind: 'yes_no',
            label: 'All hallway lights working',
            defectIf: 'no',
            required: true,
          },
        ],
      },
      {
        id: ID.common.s_stairs,
        title: 'Stairwells',
        questions: [
          { id: ID.common.q_stairs_clean, kind: 'pass_fail', label: 'Stairwells clean', required: true },
          {
            id: ID.common.q_stairs_obstruct,
            kind: 'yes_no',
            label: 'Stairwells unobstructed',
            defectIf: 'no',
            required: true,
          },
        ],
      },
      {
        id: ID.common.s_laundry,
        title: 'Laundry',
        questions: [
          { id: ID.common.q_laundry_clean, kind: 'pass_fail', label: 'Laundry room clean', required: true },
          {
            id: ID.common.q_laundry_lint,
            kind: 'yes_no',
            label: 'Lint traps cleaned',
            defectIf: 'no',
            required: true,
          },
        ],
      },
      {
        id: ID.common.s_exterior,
        title: 'Exterior',
        questions: [
          { id: ID.common.q_ext_clean, kind: 'pass_fail', label: 'Sidewalks and entry clean', required: true },
          {
            id: ID.common.q_ext_trash,
            kind: 'yes_no',
            label: 'Trash area secured and tidy',
            defectIf: 'no',
            required: true,
          },
        ],
      },
      {
        id: ID.common.s_photos,
        title: 'Photos',
        questions: [
          {
            id: ID.common.q_photos,
            kind: 'photo',
            label: 'Walkthrough photos',
            minPhotos: 1,
            required: false,
          },
        ],
      },
    ],
  },
};

const moveOutPreset: InspectionPreset = {
  slug: 'move-out-inspection',
  name: 'Move-Out Inspection',
  description: 'Per-room condition assessment when a unit is being turned over.',
  appliesTo: 'unit',
  schema: {
    sections: [
      {
        id: ID.moveout.s_kitchen,
        title: 'Kitchen',
        questions: [
          {
            id: ID.moveout.q_k_appliances,
            kind: 'pass_fail',
            label: 'Appliances clean and functional',
            required: true,
          },
          {
            id: ID.moveout.q_k_cabinets,
            kind: 'pass_fail',
            label: 'Cabinets and drawers undamaged',
            required: true,
          },
          {
            id: ID.moveout.q_k_floor,
            kind: 'pass_fail',
            label: 'Floor undamaged',
            required: true,
          },
        ],
      },
      {
        id: ID.moveout.s_bath,
        title: 'Bathroom',
        questions: [
          {
            id: ID.moveout.q_b_fixtures,
            kind: 'pass_fail',
            label: 'Fixtures clean and functional',
            required: true,
          },
          { id: ID.moveout.q_b_walls, kind: 'pass_fail', label: 'Walls undamaged', required: true },
        ],
      },
      {
        id: ID.moveout.s_living,
        title: 'Living Area',
        questions: [
          { id: ID.moveout.q_l_walls, kind: 'pass_fail', label: 'Walls undamaged', required: true },
          { id: ID.moveout.q_l_floor, kind: 'pass_fail', label: 'Floor undamaged', required: true },
        ],
      },
      {
        id: ID.moveout.s_bed,
        title: 'Bedrooms',
        questions: [
          { id: ID.moveout.q_bd_walls, kind: 'pass_fail', label: 'Walls undamaged', required: true },
          { id: ID.moveout.q_bd_floor, kind: 'pass_fail', label: 'Floor undamaged', required: true },
        ],
      },
      {
        id: ID.moveout.s_overall,
        title: 'Overall',
        questions: [
          {
            id: ID.moveout.q_o_keys,
            kind: 'yes_no',
            label: 'All keys returned',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.moveout.q_o_cleaned,
            kind: 'yes_no',
            label: 'Unit professionally cleaned',
            defectIf: 'no',
            required: true,
          },
          {
            id: ID.moveout.q_o_photos,
            kind: 'photo',
            label: 'Move-out photos',
            minPhotos: 4,
            required: true,
          },
          {
            id: ID.moveout.q_o_signature,
            kind: 'signature',
            label: 'Inspector signature',
            required: true,
          },
        ],
      },
    ],
  },
};

export const INSPECTION_PRESETS: readonly InspectionPreset[] = [
  dhsPreset,
  dhsOfficialPreset,
  fireSafetyPreset,
  commonAreaPreset,
  moveOutPreset,
];
